import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Settings as SettingsIcon, Save } from "lucide-react";
import { toast } from "sonner";
import type { SettingDef } from "@/types";

const SETTING_DEFS: SettingDef[] = [
  { key: "fine_per_day", label: "Fine Per Day ($)", description: "Amount charged per day for overdue books", type: "number" },
  {
    key: "max_books_per_student",
    label: "Max Books Per Student",
    description: "Maximum number of books a student can borrow at once",
    type: "number",
  },
  { key: "issue_duration_days", label: "Issue Duration (Days)", description: "Default number of days a book can be borrowed", type: "number" },
  {
    key: "reservation_expiry_days",
    label: "Reservation Expiry (Days)",
    description: "Days before a reservation expires if not collected",
    type: "number",
  },
  { key: "barcode_prefix", label: "Barcode Prefix", description: "Prefix used for generating barcodes", type: "text" },
];

export default function SettingsPage() {
  const { api } = useAuth();
  const [settings, setSettings] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get("/settings");
        const map: Record<string, string> = {};
        res.data.forEach((s: { key: string; value: string }) => {
          map[s.key] = s.value;
        });
        setSettings(map);
      } catch {
        toast.error("Failed to load settings");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [api]);

  const handleSave = async () => {
    setSaving(true);
    try {
      for (const [key, value] of Object.entries(settings)) {
        await api.put(`/settings/${key}`, { value: String(value) });
      }
      toast.success("Settings saved");
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center py-20 text-muted-foreground">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Settings
          </h1>
        </div>
        <Button className="bg-primary hover:bg-primary/90" onClick={handleSave} disabled={saving} data-testid="save-settings-btn">
          <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <SettingsIcon className="w-5 h-5 text-primary" /> Library Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {SETTING_DEFS.map((def) => (
            <div key={def.key} className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-start">
              <div className="sm:col-span-1">
                <Label className="font-medium">{def.label}</Label>
                <p className="text-xs text-muted-foreground mt-0.5">{def.description}</p>
              </div>
              <div className="sm:col-span-2">
                <Input
                  type={def.type}
                  value={settings[def.key] || ""}
                  onChange={(e) => setSettings({ ...settings, [def.key]: e.target.value })}
                  className="max-w-xs bg-card"
                  data-testid={`setting-${def.key}`}
                />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
